var _excluded = ["children", "classes", "setState", "state", "toggleLabel"];

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import * as React from 'react';
import { tabbable } from 'tabbable';
import { styled } from '@mui/material/styles';
import { ClickAwayListener, Drawer as MuiDrawer, Switch, Divider } from '@mui/material';
import { FormControlLabel } from '../FormControlLabel';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var States;

(function (States) {
  States["Closed"] = "CLOSED";
  States["Open"] = "OPEN";
  States["Overlay"] = "OVERLAY";
})(States || (States = {}));

export var MOUSE_ENTER_DELAY = 300;

var drawerCloseStyles = function drawerCloseStyles(theme) {
  return {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    width: theme.sizing.leftDrawerWidthClosed
  };
};

var drawerOpenStyles = function drawerOpenStyles(theme) {
  return {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    width: theme.sizing.leftDrawerWidthOpen
  };
};

var Root = styled(MuiDrawer, {
  label: 'MnrlDrawer-root'
})(function (_ref) {
  var _extends2;

  var _ref$ownerState = _ref.ownerState,
      isClosed = _ref$ownerState.isClosed,
      isOverlay = _ref$ownerState.isOverlay,
      isOpen = _ref$ownerState.isOpen,
      theme = _ref.theme;
  return _extends({
    flexShrink: 0
  }, (isClosed || isOverlay) && _extends({}, drawerCloseStyles(theme), {
    overflowX: 'hidden'
  }), isOpen && drawerOpenStyles(theme), {
    '& .MuiDrawer-paper': _extends((_extends2 = {
      borderRight: "1px solid " + theme.palette.divider,
      borderRadius: 0,
      overflowX: 'hidden',
      padding: "0 " + theme.sizing.drawerGutter,
      // Numbers and media queries from `theme.mixins.toolbar`
      // "+ 1"s are to accommodate the border on AppBar
      height: "calc(100% - " + (56 + 1) + "px)",
      top: 56 + 1
    }, _extends2[theme.breakpoints.up('xs') + " and (orientation: landscape)"] = {
      height: "calc(100% - " + (48 + 1) + "px)",
      top: 48 + 1
    }, _extends2[theme.breakpoints.up('sm')] = {
      height: "calc(100% - " + (64 + 1) + "px)",
      top: 64 + 1
    }, _extends2), isClosed && _extends({}, drawerCloseStyles(theme), {
      boxShadow: 'none'
    }), (isOverlay || isOpen) && drawerOpenStyles(theme), isOverlay && {
      border: 0,
      boxShadow: theme.shadows[3],
      clipPath: "inset(0 -8px 0 0)" // Allow for box-shadow on right

    })
  });
});
var Footer = styled('div', {
  label: 'MnrlDrawer-footer'
})(function (_ref2) {
  var isClosed = _ref2.ownerState.isClosed,
      theme = _ref2.theme;
  return isClosed ? theme.mixins.visuallyHidden : {
    marginTop: 'auto'
  };
});
var StyledFormControlLabel = styled(FormControlLabel, {
  label: 'MnrlDrawer-formControlLabel'
})(function (_ref3) {
  var theme = _ref3.theme;
  return {
    display: 'flex',
    minHeight: parseInt(theme.sizing.footerHeight) - 1,
    padding: theme.spacing(1, 1, 1, 0),
    justifyContent: 'space-between',
    '& .MuiFormControlLabel-label': {
      whiteSpace: 'nowrap'
    }
  };
});

var hasEligibleAncestor = function hasEligibleAncestor(element, farthestAncestor) {
  var _farthestAncestor;

  if (farthestAncestor === void 0) {
    farthestAncestor = document.body;
  }

  if (!element || element.dataset.drawerOptOut) {
    return false;
  }

  if (element.dataset.drawerOptIn) {
    return true;
  }

  var parent = element.parentElement;

  if (parent && (_farthestAncestor = farthestAncestor) != null && _farthestAncestor.contains(parent)) {
    return hasEligibleAncestor(parent, farthestAncestor);
  }

  return true;
};

export var Drawer = function Drawer(_ref4) {
  var children = _ref4.children,
      classesProp = _ref4.classes,
      setState = _ref4.setState,
      state = _ref4.state,
      _ref4$toggleLabel = _ref4.toggleLabel,
      toggleLabel = _ref4$toggleLabel === void 0 ? 'Always show menu' : _ref4$toggleLabel,
      restProps = _objectWithoutPropertiesLoose(_ref4, _excluded);

  var isClosed = state.state === States.Closed;
  var isOverlay = state.state === States.Overlay;
  var isOpen = state.state === States.Open;
  var drawer = React.useRef(null);
  var close = React.useCallback(function () {
    setState(function (prevState) {
      return prevState.state === States.Overlay ? {
        state: States.Closed
      } : {};
    });
  }, [setState]);
  var overlay = React.useCallback(function () {
    setState(function (prevState) {
      return prevState.state === States.Closed ? {
        state: States.Overlay
      } : {};
    });
  }, [setState]);
  var toggle = React.useCallback(function () {
    setState(function (prevState) {
      return prevState.state === States.Open ? {
        state: States.Overlay,
        userToggled: true
      } : {
        state: States.Open,
        userToggled: true
      };
    });
  }, [setState]);
  var handleClickAway = close;
  var mouseEnterTimer;

  var handleMouseEnter = function handleMouseEnter() {
    mouseEnterTimer = setTimeout(function () {
      overlay();
    }, MOUSE_ENTER_DELAY);
  };

  var handleMouseLeave = function handleMouseLeave() {
    clearTimeout(mouseEnterTimer);
    close();
  };

  var handleTouchend = function handleTouchend(event) {
    if (hasEligibleAncestor( // @ts-ignore - TODO: Typing DOM stuff is hard!
    event.target, drawer.current)) {
      overlay();
    }
  };

  React.useEffect(function () {
    var root = drawer.current;
    var tabbables = root ? tabbable(root) : [];
    var tabbablesLength = tabbables.length;
    var firstTabbable = tabbablesLength ? tabbables[0] : null;
    var lastTabbable = tabbablesLength ? tabbables[tabbablesLength - 1] : null;

    var handleLocalFocus = function handleLocalFocus() {
      if (root != null && root.contains(document.activeElement)) {
        overlay();
      }
    };

    var handleLocalKeydown = function handleLocalKeydown(event) {
      if (event.key === 'Escape') {
        close();
      }

      if (event.key === 'Tab' && (event.shiftKey && firstTabbable && event.target === firstTabbable || !event.shiftKey && lastTabbable && event.target === lastTabbable)) {
        close();
      }
    };

    root == null ? void 0 : root.addEventListener('focus', handleLocalFocus, true);
    root == null ? void 0 : root.addEventListener('keydown', handleLocalKeydown, true);
    return function () {
      root == null ? void 0 : root.removeEventListener('focus', handleLocalFocus);
      root == null ? void 0 : root.removeEventListener('keydown', handleLocalKeydown);
    };
  }, [close, overlay]);
  var ownerState = {
    isClosed: isClosed,
    isOverlay: isOverlay,
    isOpen: isOpen
  };
  return /*#__PURE__*/_jsx(ClickAwayListener, {
    onClickAway: handleClickAway,
    touchEvent: "onTouchEnd",
    children: /*#__PURE__*/_jsxs(Root, _extends({
      onMouseEnter: handleMouseEnter,
      onTouchEnd: handleTouchend,
      onMouseLeave: handleMouseLeave,
      open: !isClosed,
      variant: "permanent",
      ownerState: ownerState
    }, restProps, {
      ref: drawer,
      children: [children, /*#__PURE__*/_jsxs(Footer, {
        ownerState: ownerState,
        children: [/*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsx(StyledFormControlLabel, {
          control: /*#__PURE__*/_jsx(Switch, {
            checked: isOpen,
            onChange: toggle,
            value: "alwaysShowMenu"
          }),
          label: toggleLabel,
          labelPlacement: "start"
        })]
      })]
    }))
  });
};